import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { SkillTrendCard } from "@/components/prooftree/SkillTrendCard"
import { Avatar, AvatarFallbackText } from "@/components/ui/avatar"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  ContactDetails,
  ContactSkillTrend,
  ContactSuggestedAction,
  ContactTimelineItem,
  getContactDetails,
  getSkill,
  getSkillDomainColor,
  JournalEntry,
} from "@/data/prooftree"
import { getEqContactSkillTrends, listEqContactInteractions } from "@/services/eqParityBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  BarChart3,
  BookOpenText,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  CircleUserRound,
  Clock3,
  FileVideo,
  HeartHandshake,
  MessageCircle,
  Sparkles,
  Target,
  TrendingDown,
  type LucideIcon,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, ScrollView, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function ContactDetailsScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const { id, name, role, relationship, context } = useLocalSearchParams<{
    id: string
    name?: string
    role?: string
    relationship?: string
    context?: string
  }>()
  const details = isSignedIn
    ? getFallbackDetails({ id, name, role, relationship, context })
    : (getContactDetails(id) ?? getFallbackDetails({ id, name, role, relationship, context }))
  const [backendTrends, setBackendTrends] = useState<ContactSkillTrend[]>([])
  const [backendTimeline, setBackendTimeline] = useState<ContactTimelineItem[]>([])
  const [trendMessage, setTrendMessage] = useState("Using local fallback contact analytics.")
  const scrollY = useRef(new Animated.Value(0)).current

  useEffect(() => {
    let cancelled = false

    async function loadTrends() {
      const [trends, interactions] = await Promise.all([
        getEqContactSkillTrends(id),
        listEqContactInteractions(id),
      ])
      if (cancelled) return

      setBackendTimeline(interactions.mode === "supabase" ? interactions.items : [])

      if (trends.length === 0) {
        setBackendTrends([])
        setTrendMessage(interactions.message || "No Supabase contact trend history yet.")
        return
      }

      setBackendTrends(
        trends.map((trend) => ({
          skillId: trend.skillId,
          currentLevel: Math.max(1, Math.min(4, Math.ceil(trend.currentScore / 25))),
          currentScore: trend.currentScore,
          changePercent: trend.changePercent,
          direction: trend.direction,
          history: trend.history.map((point) => ({
            id: point.id,
            date: point.date.slice(0, 10),
            skillId: trend.skillId,
            score: point.score,
            interactionLabel: point.label,
          })),
          insight: `${getSkill(trend.skillId).title} ${trend.direction} trend: ${
            trend.changePercent > 0 ? "+" : ""
          }${trend.changePercent}% across recent Supabase interactions.`,
        })),
      )
      setTrendMessage(
        interactions.items.length > 0
          ? "Skill trends and interaction timeline loaded from Supabase."
          : "Skill trends loaded from Supabase contact interaction history.",
      )
    }

    loadTrends()

    return () => {
      cancelled = true
    }
  }, [id])

  if (!details) {
    return (
      <SafeAreaView
        className="flex-1"
        edges={["top"]}
        style={{ backgroundColor: Colors.dark.surface }}
      >
        <CollapsibleScreenHeader
          scrollY={scrollY}
          eyebrow="Contact Analytics"
          title="Contact not found"
          subtitle="This contact is not available in My Network anymore."
          backFallbackHref="/profile"
        />
        <VStack
          className="flex-1 px-5"
          style={{ paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING }}
        >
          <EmptyState
            icon={CircleUserRound}
            title="Contact not found"
            description="This contact is not available in My Network anymore."
          />
        </VStack>
      </SafeAreaView>
    )
  }

  const hasBackendContactHistory = backendTrends.length > 0 || backendTimeline.length > 0
  const effectiveDetails = hasBackendContactHistory
    ? {
        ...details,
        stats: {
          ...details.stats,
          totalInteractions: Math.max(details.stats.totalInteractions, backendTimeline.length),
          lastInteracted: backendTimeline[0]?.meta ?? details.stats.lastInteracted,
          status:
            backendTimeline.length > 0 ? "Supabase interaction history" : details.stats.status,
        },
        skillTrends: backendTrends.length > 0 ? backendTrends : details.skillTrends,
        practicedSkillIds:
          backendTrends.length > 0
            ? Array.from(new Set(backendTrends.map((trend) => trend.skillId)))
            : details.practicedSkillIds,
        latestConnections: backendTimeline.length > 0 ? backendTimeline : details.latestConnections,
        insight:
          backendTrends.length > 0
            ? `Supabase analytics found ${backendTrends.length} skill trend${
                backendTrends.length === 1 ? "" : "s"
              } for this contact.`
            : "Supabase interaction history is available. Add scored interactions to build skill trends.",
      }
    : details
  const { contact, stats } = effectiveDetails
  const averageQuality =
    effectiveDetails.skillTrends.length > 0
      ? Math.round(
          effectiveDetails.skillTrends.reduce((sum, trend) => sum + trend.currentScore, 0) /
            effectiveDetails.skillTrends.length,
        )
      : undefined
  const decliningTrend = effectiveDetails.skillTrends.find(
    (trend) => trend.direction === "negative",
  )

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Contact Analytics"
        title="Relationship profile"
        subtitle={`${contact.name} • ${contact.role}`}
        backFallbackHref="/profile"
        statusText={trendMessage}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 56 : 40,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 overflow-hidden rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}17`,
            borderColor: `${Colors.dark.primary}52`,
          }}
        >
          <HStack className="items-start gap-4">
            <Avatar size="2xl" className="border" style={{ borderColor: Colors.dark.primary }}>
              <AvatarFallbackText>{contact.name}</AvatarFallbackText>
            </Avatar>
            <VStack className="flex-1 gap-2">
              <Text className="text-3xl font-bold text-white">{contact.name}</Text>
              <Text className="text-sm font-bold" style={{ color: Colors.dark.textPrimary }}>
                {contact.role} • {contact.relationship}
              </Text>
              <HStack
                className="self-start rounded-full px-3 py-1.5"
                style={{ backgroundColor: `${Colors.dark.primary}24` }}
              >
                <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                  {stats.status}
                </Text>
              </HStack>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                {contact.context}
              </Text>
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                {trendMessage}
              </Text>
            </VStack>
          </HStack>
        </Box>

        <SectionTitle icon={Target} title="Contact Stats" color={Colors.dark.primary} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 mb-5 px-5">
          <HStack className="gap-3">
            <StatCard label="Interactions" value={`${stats.totalInteractions}`} />
            <StatCard
              label="Avg. quality"
              value={averageQuality ? `${averageQuality}/100` : "New"}
            />
            <StatCard label="Last interacted" value={stats.lastInteracted} />
            <StatCard label="Last proof" value={stats.lastProof} />
            <StatCard label="Mood" value={stats.averageMood} />
          </HStack>
        </ScrollView>

        <VStack className="mb-6 gap-2">
          <Text className="text-sm font-bold" style={{ color: Colors.dark.textSecondary }}>
            Skills practiced together
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 px-5">
            <HStack className="gap-2">
              {effectiveDetails.practicedSkillIds.map((skillId) => (
                <SkillChip key={skillId} skillId={skillId} compact />
              ))}
            </HStack>
          </ScrollView>
        </VStack>

        <SectionTitle icon={BarChart3} title="Skill Trends" color={Colors.dark.growth} />
        {effectiveDetails.skillTrends.length > 0 ? (
          <VStack className="mb-5 gap-3">
            {effectiveDetails.skillTrends.map((trend) => (
              <SkillTrendCard key={trend.skillId} trend={trend} />
            ))}
          </VStack>
        ) : (
          <Box
            className="mb-5 rounded-2xl border p-4"
            style={{
              backgroundColor: Colors.dark.surfaceSecondary,
              borderColor: `${Colors.dark.growth}33`,
            }}
          >
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              No interactions yet. Start coaching this person to build a visible trend.
            </Text>
          </Box>
        )}

        <Box
          className="mb-6 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}17`,
            borderColor: `${Colors.dark.primary}4D`,
          }}
        >
          <HStack className="items-start gap-3">
            <Box
              className="h-10 w-10 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.primary}29` }}
            >
              {decliningTrend ? (
                <TrendingDown color={Colors.dark.error} size={18} />
              ) : (
                <Sparkles color={Colors.dark.primary} size={18} />
              )}
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                ANALYTICS INSIGHT
              </Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
                {effectiveDetails.insight}
              </Text>
            </VStack>
          </HStack>
        </Box>

        <SectionTitle
          icon={BriefcaseBusiness}
          title="Latest Connections"
          color={Colors.dark.skill}
        />
        <VStack className="mb-6 gap-3">
          {effectiveDetails.latestConnections.map((item) => (
            <TimelineCard key={item.id} item={item} />
          ))}
        </VStack>

        <SectionTitle
          icon={BookOpenText}
          title={`Journal Records with ${contact.name}`}
          color={Colors.dark.proof}
        />
        <VStack className="mb-4 gap-3">
          {effectiveDetails.journalEntries.length > 0 ? (
            effectiveDetails.journalEntries.map((entry) => (
              <JournalRecordCard key={entry.id} entry={entry} />
            ))
          ) : (
            <Box
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: `${Colors.dark.proof}33`,
              }}
            >
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                No journal records mention {contact.name} yet. Coach one action first, then log the
                proof here.
              </Text>
            </Box>
          )}
        </VStack>
        <TouchableOpacity
          activeOpacity={0.86}
          onPress={() =>
            router.push({
              pathname: "/journal",
              params: { contactId: contact.id },
            })
          }
        >
          <Box
            className="mb-6 rounded-2xl border p-4"
            style={{
              backgroundColor: Colors.dark.surfaceSecondary,
              borderColor: `${Colors.dark.proof}44`,
            }}
          >
            <HStack className="items-center justify-between gap-3">
              <Text className="flex-1 font-bold text-white">
                View all journal entries with {contact.name}
              </Text>
              <ChevronRight color={Colors.dark.proof} size={18} />
            </HStack>
          </Box>
        </TouchableOpacity>

        <Box
          className="rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}1F`,
            borderColor: `${Colors.dark.primary}6B`,
            shadowColor: Colors.dark.primary,
            shadowOpacity: 0.18,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 10 },
          }}
        >
          <HStack className="mb-4 items-start gap-3">
            <Box
              className="h-11 w-11 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.primary}29` }}
            >
              <Sparkles color={Colors.dark.primary} size={20} />
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                COACHING OPPORTUNITIES
              </Text>
              <Text className="text-2xl font-bold text-white">
                Ways to grow skills with {contact.name}
              </Text>
            </VStack>
          </HStack>

          <VStack className="gap-3">
            {effectiveDetails.suggestedActions.map((action) => (
              <SuggestedActionCard
                key={action.id}
                action={action}
                trend={effectiveDetails.skillTrends.find(
                  (trend) => trend.skillId === action.skillId,
                )}
              />
            ))}
          </VStack>
        </Box>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function getFallbackDetails({
  id,
  name,
  role,
  relationship,
  context,
}: {
  id?: string
  name?: string
  role?: string
  relationship?: string
  context?: string
}): ContactDetails | undefined {
  if (!id || !name) return undefined

  const practicedSkillIds = ["active_listening_presence", "clear_communication_requests"]

  return {
    contact: {
      id,
      name,
      role: role ?? "Personal contact",
      frequency: "Manual",
      relationship: relationship ?? "Added by user",
      context: context ?? "Available for Coach Mode suggestions in this local fallback session.",
      skillIds: practicedSkillIds,
    },
    stats: {
      totalInteractions: 0,
      lastInteracted: "New",
      lastProof: "No proof yet",
      averageMood: "Neutral",
      status: "Added to My Network",
    },
    skillTrends: [],
    insight: "No interactions yet. Start coaching this person to build a visible trend.",
    practicedSkillIds,
    relatedGoals: [],
    relatedTasks: [],
    latestConnections: [
      {
        id: `manual-${id}`,
        type: "Event",
        title: "Added to My Network",
        meta: "Manual contact",
      },
    ],
    journalEntries: [],
    suggestedActions: [
      {
        id: `manual-${id}-listen`,
        title: "Ask one focused follow-up question",
        skillId: "active_listening_presence",
        advice: "Use this contact for a small listening rep before adding richer history.",
      },
      {
        id: `manual-${id}-request`,
        title: "Make one clear request",
        skillId: "clear_communication_requests",
        advice: "Turn the next conversation into a simple action, owner, and timing.",
      },
    ],
  }
}

function SectionTitle({
  icon: Icon,
  title,
  color,
}: {
  icon: LucideIcon
  title: string
  color: string
}) {
  return (
    <HStack className="mb-3 items-center gap-2">
      <Icon color={color} size={18} />
      <Text className="text-xl font-bold text-white">{title}</Text>
    </HStack>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Box
      className="min-h-[96px] w-40 justify-between rounded-2xl border p-4"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${Colors.dark.primary}38`,
      }}
    >
      <Text className="text-xs font-bold" style={{ color: Colors.dark.textSecondary }}>
        {label}
      </Text>
      <Text className="text-xl font-bold text-white" numberOfLines={2}>
        {value}
      </Text>
    </Box>
  )
}

function TimelineCard({ item }: { item: ContactTimelineItem }) {
  const color =
    item.type === "Goal"
      ? Colors.dark.growth
      : item.type === "Task"
        ? Colors.dark.proof
        : Colors.dark.skill

  return (
    <Box
      className="rounded-2xl border p-4"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${color}3D`,
      }}
    >
      <HStack className="items-start gap-3">
        <Box
          className="mt-1 h-3 w-3 rounded-full"
          style={{
            backgroundColor: color,
            shadowColor: color,
            shadowOpacity: 0.35,
            shadowRadius: 8,
          }}
        />
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color }}>
            {item.type}
          </Text>
          <Text className="font-bold text-white">{item.title}</Text>
          <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
            {item.meta}
            {item.status ? ` • ${item.status}` : ""}
          </Text>
        </VStack>
      </HStack>
    </Box>
  )
}

function JournalRecordCard({ entry }: { entry: JournalEntry }) {
  const primarySkillId = entry.skillIds[0]
  const skill = getSkill(primarySkillId)
  const color = getSkillDomainColor(primarySkillId)

  return (
    <PressableScale onPress={() => router.push("/journal")}>
      <Box
        className="rounded-2xl border p-4"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: `${color}44`,
        }}
      >
        <HStack className="items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${color}22` }}
          >
            {entry.videoThumbnail ? (
              <FileVideo color={color} size={19} />
            ) : (
              <MessageCircle color={color} size={19} />
            )}
          </Box>
          <VStack className="flex-1 gap-2">
            <HStack className="items-center gap-2">
              <CalendarDays color={color} size={14} />
              <Text className="text-xs font-bold" style={{ color }}>
                {entry.date} • {skill.title}
              </Text>
            </HStack>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {entry.videoThumbnail ? `${entry.videoThumbnail}: ` : ""}
              {entry.text}
            </Text>
          </VStack>
        </HStack>
      </Box>
    </PressableScale>
  )
}

function SuggestedActionCard({
  action,
  trend,
}: {
  action: ContactSuggestedAction
  trend?: ContactSkillTrend
}) {
  const color = getSkillDomainColor(action.skillId)
  const trendColor =
    trend?.direction === "negative"
      ? Colors.dark.error
      : trend?.direction === "positive"
        ? Colors.dark.growth
        : Colors.dark.textSecondary
  const trendCopy = trend
    ? `${trend.changePercent > 0 ? "+" : ""}${trend.changePercent}% trend across recent interactions`
    : "Trend will appear after a few interactions"

  return (
    <Box
      className="rounded-2xl border p-4"
      style={{
        backgroundColor: Colors.dark.surface,
        borderColor: `${color}4D`,
      }}
    >
      <VStack className="gap-3">
        <HStack className="items-start gap-3">
          <Box
            className="h-10 w-10 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${color}22` }}
          >
            <HeartHandshake color={color} size={18} />
          </Box>
          <VStack className="flex-1 gap-2">
            <Text className="text-lg font-bold text-white">{action.title}</Text>
            <SkillChip skillId={action.skillId} compact />
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {action.advice}
            </Text>
            <HStack
              className="self-start rounded-full px-3 py-1.5"
              style={{ backgroundColor: `${trendColor}1F` }}
            >
              <Text className="text-xs font-bold" style={{ color: trendColor }}>
                {trendCopy}
              </Text>
            </HStack>
          </VStack>
        </HStack>

        <Button
          size="lg"
          className="rounded-2xl"
          disabled={!action.coachActionId}
          style={{
            backgroundColor: Colors.dark.primary,
            opacity: action.coachActionId ? 1 : 0.58,
          }}
          onPress={() => {
            if (action.coachActionId) {
              router.push({
                pathname: "/coach/action/[id]",
                params: { id: action.coachActionId },
              })
            }
          }}
        >
          <Clock3 color={Colors.dark.primaryForeground} size={17} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            Coach this action
          </ButtonText>
        </Button>
      </VStack>
    </Box>
  )
}
